import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import ReactLoading from "react-loading"
import { addCommas } from "@/lib/addCommas"
import Image from "next/image"
import { FaRegSquareMinus } from "react-icons/fa6"
import { FaRegSquarePlus } from "react-icons/fa6"
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks/reduxHooks"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { contributeSeasonPassPayload } from "@/types/types.dashboard"
import { contributeToSeasonPass } from "@/app/dashboard/dashboardAction"

type Props = {
    credits: number
}

export const SeasonContributionComponent: React.FC<Props> = (props) => {
    const rank = useAppSelector((state) => state.dashboard.rank)
    const nextRank = useAppSelector((state) => state.dashboard.nextRank)
    const playerId = useAppSelector((state) => state.auth.playFabId)
    const playerTitleId = useAppSelector((state) => state.auth.playerTitleId)
    const creditsUntilPromotion = useAppSelector((state) => state.dashboard.creditsUntilPromotion)
    const [contribution, setContribution] = useState<number>(1000)
    const [disabled, setDisabled] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const seasonPass = useAppSelector((state) => state.auth.seasonPass)
    const [open, setOpen] = useState<boolean>(false)
    const dispatch = useAppDispatch()
    const { credits } = props

    function nextRankIcon(rank: string) {
        switch (rank) {
            case "Unranked":
                return "/Icon_TierBadge_Bronze.svg"
            case "Bronze":
                return "/Icon_TierBadge_Silver.svg"
            case "Silver":
                return "/Icon_TierBadge_Gold.svg"
            case "Gold":
                return "/Icon_TierBadge_Gold.svg"
            default:
                return ""
        }
    }

    const increment = () => {
        if (contribution < 5000) {
            setContribution((prev) => prev + 1000)
        }
    }

    const decrement = () => {
        if (contribution > 1000) {
            setContribution((prev) => prev - 1000)
        }
    }

    useEffect(() => {
        if (credits < contribution) {
            setDisabled(true)
        } else {
            setDisabled(false)
        }
    }, [credits, contribution])

    const initConversion = async () => {
        setSubmitting(true)
        try {
            const payload: contributeSeasonPassPayload = {
                playerId: playerId!,
                playerTitleId: playerTitleId!,
                creditAmount: contribution,
            }
            const response = await contributeToSeasonPass(payload)
            process.env.NEXT_PUBLIC_ENV === "development" && console.log("contributionResponse::: ", response)
            if (response.success) {
                const newCredits = credits - contribution
                dispatch({ type: "auth/setCredits", payload: newCredits })
                dispatch({ type: "auth/setSeasonPass", payload: seasonPass! + contribution })
                setOpen(false)
                if (creditsUntilPromotion! <= contribution) {
                    dispatch({ type: "dashboard/setPromoModal", payload: true })
                    toast.success(`Congrats! You have reached the next tier!`)
                } else {
                    toast.success(`Successfully contributed ${addCommas(contribution)} credits to your Season Pass!`)
                }
            }
        } catch (error) {
            console.warn("Error converting shards to credits", error)
            toast.error(`${error}`)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <AlertDialog open={open}>
                <AlertDialogTrigger asChild onClick={() => setOpen((prev) => !prev)}>
                    <button className="mt-8 transition-all py-1 px-3 font-normal bg-primary text-black">
                        CONTRIBUTE CREDITS
                    </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-black border-none text-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[23px]">Tier Promotion - Contribute Credits</AlertDialogTitle>
                        <section className="font-thin text-[17px]">
                            <div className="grid gird-cols-1 sm:grid-cols-2">
                                <div>
                                    <p>
                                        You can contribute credits to the season pass to unlock exclusive rewards and
                                        upgrade your tier. Each tier has unique benefits when playing MetalCore and
                                        MetalCore Missions.
                                    </p>
                                    <p className="mt-5 text-[14px] italic">
                                        You must contribute to your season pass in increments of 1,000 credits. Maximum
                                        5,000 credits per contribution.
                                    </p>
                                    <section className="my-5 border-t border-b py-3 border-[#555] grid grid-cols-2">
                                        <div>
                                            <h2>Your Credits</h2>
                                            <div className="text-primary">{addCommas(credits) ?? 0}</div>
                                        </div>
                                        <div>
                                            <h2>Remaining for Promotion</h2>
                                            <div className="text-primary">
                                                {creditsUntilPromotion ? addCommas(creditsUntilPromotion) : 0}
                                            </div>
                                        </div>
                                    </section>
                                    <section className="flex">
                                        <div>
                                            <button
                                                className={`cursor-pointer ${
                                                    contribution === 1000 ? "hover:text-red-500" : "hover:text-primary"
                                                } transition-all`}
                                                onClick={() => decrement()}
                                            >
                                                <FaRegSquareMinus className="text-[40px]" />
                                            </button>
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                disabled
                                                className="h-[38px] px-2 mx-2 rounded text-center text-black font-bold text-[24px] disabled:text-black w-[80px]"
                                                placeholder={addCommas(contribution).toString()}
                                            />
                                        </div>
                                        <div>
                                            <button
                                                className={`cursor-pointer ${
                                                    contribution === 5000 ? "hover:text-red-500" : "hover:text-primary"
                                                } transition-all`}
                                                onClick={() => increment()}
                                            >
                                                <FaRegSquarePlus className="text-[40px]" />
                                            </button>
                                        </div>
                                    </section>
                                </div>
                                {rank ? (
                                    <div>
                                        <h2 className="text-center uppercase text-[15px]">You are currently {rank}</h2>
                                        <h2 className="text-center uppercase">
                                            {rank !== "Gold" ? `Advance to ${nextRank}` : rank}
                                        </h2>
                                        <div>
                                            <Image
                                                src={nextRankIcon(rank!)}
                                                width="125"
                                                height="125"
                                                alt="Next Rank"
                                                className="block mx-auto my-2"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center">
                                        <ReactLoading type={"spin"} color={"#fff"} height={60} width={60} />
                                    </div>
                                )}
                            </div>
                        </section>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            className="hover:text-black hover:border-primary"
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="text-black disabled:cursor-not-allowed bg-primary rounded px-2 disabled:bg-slate-400"
                            disabled={disabled || submitting || rank == "Gold"}
                            onClick={() => initConversion()}
                        >
                            {disabled
                                ? "Not enough credits"
                                : submitting
                                ? "Processing"
                                : rank == "Gold"
                                ? "Highest rank achieved"
                                : "Submit"}
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
